import { PLAN_FEATURES, PLAN_NAME, PLAN_SUMMARY, type PlanCode, type PlanOption } from '@content-removal/shared';

/** The line over a plan's name, and the tag beside it: what each one is for, in a word or two. */
const EYEBROW: Partial<Record<PlanCode, string>> = {
  essential: 'Start here',
  pro: 'Built to remove',
  premium: 'Full coverage',
  agency_starter: 'Start here',
  agency_growth: 'Built to scale',
  agency_scale: 'Full coverage',
};

/** What a column's button asks for: a checkout, or the team. */
export type PlanChoice = { plan: PlanOption; action: 'buy' | 'sales' };

/** What a plan includes: its public card for a creator plan, the fixed list for an agency one. */
export function planFeatureRows(plan: PlanOption): { has: Array<{ label: string; value?: string }>; no: string[] } {
  if (plan.cardRows) {
    return {
      has: plan.cardRows.filter((row) => !row.off).map((row) => ({ label: row.label, value: row.value })),
      no: plan.cardRows.filter((row) => row.off).map((row) => row.label),
    };
  }
  const fixed = PLAN_FEATURES[plan.code as PlanCode] ?? { has: [] };
  return { has: fixed.has.map((label) => ({ label })), no: fixed.no ?? [] };
}

/**
 * One kind of plan, creator or agency, side by side. Without state, so the
 * marketing site renders it on the server with links (`href`) and the app
 * renders it with buttons (`onChoose`).
 */
export function PlanColumns({
  plans,
  kind,
  current,
  currentInterval,
  cycle,
  busy,
  riskUsd,
  onChoose,
  href,
}: {
  plans: PlanOption[];
  kind: 'creator' | 'agency';
  current?: string | null;
  /** The cycle the account is billed on. Without it, the current plan is the code alone. */
  currentInterval?: 'month' | 'year' | null;
  /** Which prices and which checkout the columns are showing. Agency ignores year. */
  cycle?: 'month' | 'year';
  busy?: string | null;
  riskUsd?: number;
  onChoose?: (choice: PlanChoice) => void;
  /** Where every button leads when there is nothing to call, as on the public site. */
  href?: string;
}) {
  const shown = plans.filter((one) => one.kind === kind);
  return (
    <div className={`plan-cols plan-cols-${shown.length}`}>
      {shown.map((plan) => {
        const code = plan.code as PlanCode;
        const rows = planFeatureRows(plan);
        const features = {
          has: rows.has.map((row) => (row.value ? `${row.label}: ${row.value}` : row.label)),
          no: rows.no,
        };
        const best = code === 'pro' || code === 'agency_growth';
        /* Yearly is a creator price (annualUsd, already −20%). An agency column
           has none, and a missing annual price must not pretend to be one. */
        const yearly = kind === 'creator' && cycle === 'year' && plan.annualUsd != null;
        const amount = yearly ? plan.annualUsd : plan.priceUsd;
        const share = kind === 'creator' && !yearly && riskUsd && plan.priceUsd
          ? `about ${Math.max(1, Math.round((plan.priceUsd / riskUsd) * 100))}% of what is at risk`
          : null;
        const onThisPlan = current === code;
        /* Same plan, other cycle: still their plan, but the button has to be
           able to move the billing. A disabled "Current plan" could not. */
        const switchCycle = onThisPlan && currentInterval != null && yearly !== (currentInterval === 'year') && (cycle !== 'year' || plan.annualUsd != null);
        const isCurrent = onThisPlan && !switchCycle;
        const action: PlanChoice['action'] = kind === 'agency' ? 'sales' : 'buy';
        const label = busy === code
          ? action === 'sales' ? 'Sending…' : 'Opening…'
          : switchCycle
            ? cycle === 'year' ? 'Switch to yearly' : 'Switch to monthly'
            : action === 'buy' ? `Start with ${PLAN_NAME[code]}` : plan.salesOnly ? 'Talk to sales' : 'Book a pilot';
        const tone = `btn btn-block${plan.salesOnly ? '' : ' btn-primary'}`;
        const from = plan.minProfiles > 0 && plan.priceUsd != null && plan.perProfileUsd != null && !plan.salesOnly
          ? `, from ~$${(plan.priceUsd + plan.perProfileUsd * plan.minProfiles).toLocaleString('en-US')}/mo`
          : '';
        return (
          <div key={code} className={`plan-col plan-col-${code}${best ? ' is-best' : ''}${onThisPlan ? ' is-current' : ''}`}>
            <div className="plan-col-head">
              <div>
                {EYEBROW[code] && <span className="plan-col-eyebrow">{EYEBROW[code]}</span>}
                <b className="plan-col-name">{PLAN_NAME[code]}</b>
              </div>
              {best ? (
                <span className="plan-col-badge">{kind === 'creator' ? 'Most popular' : 'Best value'}</span>
              ) : code === 'premium' ? (
                <span className="plan-col-badge is-quiet">Most protection</span>
              ) : null}
            </div>
            <div className="plan-col-price">
              <strong>{amount == null ? 'Soon' : `$${amount.toLocaleString('en-US')}${!yearly && plan.salesOnly ? '+' : ''}`}</strong>
              <span>{yearly ? '/yr' : `/mo${plan.perProfileUsd != null ? (plan.salesOnly ? ' + custom per-profile rate' : ` + $${plan.perProfileUsd}/profile`) : ''}`}</span>
            </div>
            <span className="plan-col-for">{PLAN_SUMMARY[code]}{from}</span>
            {share ? <span className="plan-col-share">{share}</span> : <span />}
            <ul className="plan-col-feats">
              {features.has.map((line) => <li key={line}><Tick />{line}</li>)}
              {(features.no ?? []).map((line) => <li key={line} className="is-off"><Cross />{line}</li>)}
            </ul>
            {isCurrent ? (
              <button type="button" className="btn btn-block" disabled>Current plan</button>
            ) : href ? (
              <a className={tone} href={href}>{label}</a>
            ) : (
              <button type="button" className={tone} disabled={busy === code} onClick={() => onChoose?.({ plan, action })}>
                {label}
              </button>
            )}
            {kind === 'agency' && !plan.salesOnly && !isCurrent && !href ? (
              <button type="button" className="plan-col-quiet" onClick={() => onChoose?.({ plan, action: 'buy' })}>
                or buy now
              </button>
            ) : <span />}
          </div>
        );
      })}
    </div>
  );
}

function Tick() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" /></svg>;
}

function Cross() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" /></svg>;
}
